/**
 * Attribution Service
 * Handles storing and retrieving attribution parameters for A/B testing and conversion tracking
 */

import { devLog, devWarn, getCookie } from "./util";

export interface AttributionData {
  abTestSource?: string;
  signUpSource?: string;
  urlReferrer?: string;
}

const ATTRIBUTION_STORAGE_KEY = "cm_attribution";
const ATTRIBUTION_EXPIRY_KEY = "cm_attribution_expiry";
const ATTRIBUTION_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export class AttributionService {
  /**
   * Routes every signup passes through regardless of where it came from. They name the step, not
   * the origin, so they must never become a signup source. See isLandingPath.
   */
  private static readonly AUTH_PATHS = [
    "signup",
    "login",
    "admin-login",
    "external-login",
    "v1-login",
    "forgot-password",
    "reset-password",
  ];

  /**
   * True when a path is a real landing page and may name itself as the signup source.
   *
   * V1 has always excluded auth routes this way (cm.attribution.js deriveSource folds them to "/").
   * V2 did not, so a visitor who reached /signup carrying no campaign param was recorded with
   * signup-source "signup" - a label that describes the form, not the acquisition. Worse, because
   * AttributionCapture spreads the freshly-extracted value over the stored one, simply loading
   * /signup overwrote a real source captured earlier in the same visit.
   */
  private static isLandingPath(path: string): boolean {
    if (!path) return false;
    const first = path.split("/")[0].toLowerCase();
    return !this.AUTH_PATHS.includes(first);
  }

  /**
   * Check if referrer is external (not internal domains)
   */
  private static isExternalReferrer(referrer: string): boolean {
    if (!referrer) return false;

    const internalDomains = [
      "catalogmachine.com",
      "localhost",
      "127.0.0.1",
      "accounts.google.com",
    ];

    return !internalDomains.some((domain) =>
      referrer.toLowerCase().includes(domain.toLowerCase())
    );
  }

  /**
   * Clean referrer URL to extract only host and path (similar to V1 logic)
   */
  private static cleanReferrer(referrerUrl: string): string {
    try {
      const url = new URL(referrerUrl);
      const cleaned = url.host + url.pathname;

      // Only return cleaned referrer if it's external
      return this.isExternalReferrer(cleaned) ? cleaned : "";
    } catch {
      // If URL parsing fails, return empty string
      return "";
    }
  }

  /**
   * Extract attribution parameters from URL search params and V1 cookies
   */
  static extractFromUrl(searchParams: URLSearchParams): AttributionData {
    const attribution: AttributionData = {};

    // Extract and derive urlReferrer
    const referrerParam = searchParams.get("referrer");
    let rawReferrer = "";
    if (referrerParam) {
      rawReferrer = decodeURIComponent(referrerParam);
    } else if (document.referrer) {
      rawReferrer = document.referrer;
    } else {
      rawReferrer = window.location.href;
    }

    // Clean the referrer (remove protocol, query params, etc.) and filter out internal ones
    if (rawReferrer) {
      const cleanedReferrer = this.cleanReferrer(rawReferrer);
      if (cleanedReferrer) {
        attribution.urlReferrer = cleanedReferrer;
      }
    }

    // Extract and derive signUpSource
    const sectionParam = searchParams.get("section");
    const srcParam = searchParams.get("src");

    let source = "";
    if (sectionParam) {
      source = decodeURIComponent(sectionParam);
    } else if (srcParam) {
      source = decodeURIComponent(srcParam);
    } else {
      // Only a real landing page may name itself. Leaving source empty here leaves the key unset,
      // which preserves any source already stored; an account with nothing stored falls back to
      // "/" server-side (SignupService), matching V1.
      const path = window.location.pathname.replace(/^\//, "");
      source = this.isLandingPath(path) ? path : "";
    }

    if (source) {
      attribution.signUpSource = source;
    }

    // Extract and derive abTestSource
    const abParam = searchParams.get("ab");
    if (abParam) {
      attribution.abTestSource = abParam;
    }

    // Note: V2 should always assign 'cm2' as abGroup when abTestSource is present
    // We don't read V1 cm_ab_group cookies as V2 manages its own AB groups

    // Also try to get V1 attribution cookies if V2 localStorage is empty
    if (!attribution.urlReferrer) {
      const v1Referrer = getCookie("cm_original_referrer");
      if (v1Referrer && this.isExternalReferrer(v1Referrer)) {
        attribution.urlReferrer = v1Referrer;
      }
    }

    if (!attribution.signUpSource) {
      const v1Source = getCookie("cm_original_source");
      if (v1Source) {
        attribution.signUpSource = v1Source;
      }
    }

    return attribution;
  }

  /**
   * Store attribution data in localStorage with expiry
   */
  static store(attribution: AttributionData): void {
    try {
      const expiryTime = Date.now() + ATTRIBUTION_TTL;
      localStorage.setItem(
        ATTRIBUTION_STORAGE_KEY,
        JSON.stringify(attribution)
      );
      localStorage.setItem(ATTRIBUTION_EXPIRY_KEY, expiryTime.toString());

      devLog("Attribution data stored:", attribution);
    } catch (error) {
      devWarn("Failed to store attribution data:", error);
    }
  }

  /**
   * Retrieve attribution data from localStorage
   */
  static retrieve(): AttributionData | null {
    try {
      const expiryTime = localStorage.getItem(ATTRIBUTION_EXPIRY_KEY);

      // Check if expired
      if (expiryTime && Date.now() > parseInt(expiryTime)) {
        this.clear();
        return null;
      }

      const stored = localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      devWarn("Failed to retrieve attribution data:", error);
      return null;
    }
  }

  /**
   * Clear attribution data from localStorage
   */
  static clear(): void {
    try {
      localStorage.removeItem(ATTRIBUTION_STORAGE_KEY);
      localStorage.removeItem(ATTRIBUTION_EXPIRY_KEY);
    } catch (error) {
      devWarn("Failed to clear attribution data:", error);
    }
  }

  /**
   * Merge attribution data with existing data, preferring new values
   */
  // static merge(
  //   existing: AttributionData,
  //   new_data: AttributionData
  // ): AttributionData {
  //   return {
  //     ...existing,
  //     ...new_data,
  //   };
  // }

  /**
   * Convert attribution data to URL search parameters string
   * Maps derived parameters back to their original URL parameter names
   */
  static toUrlParams(attribution: AttributionData): string {
    const params = new URLSearchParams();

    // Map derived parameters back to original URL parameter names
    // Note: URLSearchParams.set() automatically handles encoding, so don't double-encode
    if (attribution.urlReferrer) {
      params.set("referrer", attribution.urlReferrer);
    }
    if (attribution.signUpSource) {
      params.set("src", attribution.signUpSource);
    }
    if (attribution.abTestSource) {
      params.set("ab", attribution.abTestSource);
    }

    return params.toString();
  }

  /**
   * Store current URL parameters and return complete attribution data
   */
  static storeFromCurrentUrl(searchParams: URLSearchParams): AttributionData {
    const extracted = this.extractFromUrl(searchParams);
    const existing = this.retrieve();
    const merged = existing ? { ...extracted, ...existing } : extracted;

    this.store(merged);
    return merged;
  }

  /**
   * Get attribution parameters for including in external auth URLs
   */
  static getForExternalAuth(): string {
    const attribution = this.retrieve();
    const params = attribution ? this.toUrlParams(attribution) : "";

    devLog("Attribution params for external auth:", params);
    return params;
  }
}
